import { useEffect, useRef } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { claimLongPingRunNotification, getLongPingWatchList, removeLongPingWatch } from '@bumble/app/utils/long-ping-watch';
import { notifyLongPingResult } from '@bumble/app/utils/long-ping-notify';

const POLL_INTERVAL_MS = 15000;
const isTerminalStatus = (status?: string) => ['success', 'completed', 'interrupted', 'failed'].includes(status || '');

// Mounted once at the app root so a Long Ping test that finishes while the
// user is on another page (or has logged back in after leaving) still
// surfaces a notification, instead of relying on the Long Ping dialog
// itself staying open and mounted.
export const LongPingNotifier = () => {
  const api = useApi();
  const checkingRef = useRef(false);

  useEffect(() => {
    const checkWatches = async () => {
      if (checkingRef.current) return;
      const watches = getLongPingWatchList();
      if (watches.length === 0) return;

      checkingRef.current = true;
      try {
        for (const { cpeId, cpeName } of watches) {
          const label = cpeName || cpeId;
          try {
            const response = await api.getAsyncLongPingResultApiV1GetAsyncLongPingResultGet({ node_id: cpeId });
            const { status, result, document_id, start_time_utc } = response.data;

            if (isTerminalStatus(status)) {
              removeLongPingWatch(cpeId);
              const runId = `${cpeId}:${document_id || start_time_utc || ''}`;
              if (claimLongPingRunNotification(runId)) {
                notifyLongPingResult(label, status, result);
              }
            }
          } catch (e: any) {
            // No record for this CPE (e.g. deleted, or the user was never
            // authorized) — stop watching it rather than retry forever.
            if (e?.status === 404) {
              removeLongPingWatch(cpeId);
            }
          }
        }
      } finally {
        checkingRef.current = false;
      }
    };

    checkWatches();
    const interval = setInterval(checkWatches, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [api]);

  return null;
};
